import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import defibMain from '../Images/defibrillator.jpeg';
import heroImage from '../Images/defibrilatorpage.jpg';
import Footer from '../Pages/Footer';

const imageDetails = {
  [defibMain]: {
    category: 'Defibrillators & Patient Monitors',
    heading: 'Advanced Defibrillator & Patient Monitoring System',
    title: 'Model: MT DF 200',
    description:
      'MT DF 200 is a compact and high-performance defibrillator and patient monitoring system designed for emergency cardiac care, continuous monitoring, and rapid response applications. With intelligent monitoring functions, optional advanced modules, and reliable power backup, it delivers accurate performance for hospitals, ICUs, ambulances, and critical care environments.',
    sections: {
      "Smart Display & Monitoring": {
        icon: "🖥️",
        items: [
          '7" Color TFT display',
          "Display area: 150 mm × 90 mm",
          "Clear real-time monitoring and waveform visualization",
          "Graphical and tabular trend review up to 24 hours",
          "Easy 1-2-3 front panel keyboard operation",
        ],
      },

      "Monitoring Modes & Waveforms": {
        icon: "📈",
        items: [
          "ECG Monitoring",
          "SpO₂ Monitoring (optional)",
          "NIBP Monitoring (optional)",
          "AED Mode (optional)",
          "Pacer Mode (optional)",
          "12-lead ECG (optional)",
        ],
      },

      "ECG & Patient Analysis": {
        icon: "❤️",
        items: [
          "Accurate ECG waveform monitoring",
          "Optional 12-lead ECG acquisition",
          "Real-time patient parameter display",
          "Continuous monitoring for critical patients",
          "Reliable cardiac rhythm analysis",
        ],
      },

      "Printing & Data Management": {
        icon: "🖨️",
        items: [
          "Integrated printer support",
          "Easy report generation",
          "Stores and reviews monitoring trends",
          "Quick access to patient monitoring information",
          "Suitable for hospital workflow management",
        ],
      },

      "Power & Reliability": {
        icon: "🔋",
        items: [
          "Operates on mains and rechargeable battery",
          "Battery backup:",
          "Up to 4 hours in monitor mode",
          "Up to 3 hours in defibrillator mode",
          "Stable and reliable emergency operation",
          "Designed for continuous medical use",
        ],
      },

      "Environmental & Operating Conditions": {
        icon: "🌡️",
        items: [
          "Operating Temperature: 5°C to +45°C",
          "Storage Temperature: -5°C to +50°C",
          "Humidity support: Up to 95% RH",
          "Robust and durable medical-grade design",
        ],
      },
    },
  },
};

const DefibrillatorDetail = () => {
  const allImages = [defibMain];
  const [mainImage, setMainImage] = useState(defibMain);
  const [relatedImages, setRelatedImages] = useState(allImages.filter((img) => img !== defibMain));
  const [isMobile, setIsMobile] = useState(false);
  const introRef = useRef(null);

  useLayoutEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageClick = (clickedImage) => {
    setMainImage(clickedImage);
    setRelatedImages(allImages.filter((img) => img !== clickedImage));
    setTimeout(() => {
      introRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const detail = imageDetails[mainImage];
  const sections = detail?.sections || {};

  return (
    <>
      <Helmet>
        <title>Defibrillator Machine | Mindron Meditech</title>
        <meta
          name="description"
          content="Explore MT DF 200 defibrillator machine by Mindron Meditech for emergency cardiac care, ECG monitoring, patient monitoring, AED mode, pacer mode, printing, and critical care support."
        />
        <link rel="canonical" href="https://www.mindronmeditech.com/products/defibrillator" />
      </Helmet>

      <div style={{ fontFamily: 'sans-serif', margin: 0, padding: 0, overflowX: 'hidden' }}>

        {/* ── HERO ── */}
        <div style={{
          height: isMobile ? '30vh' : '80vh',
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <img
            src={heroImage}
            alt="Hero"
            style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(0,20,60,0.5) 0%, transparent 60%)',
          }} />
        </div>

        {/* ── PRODUCT INTRO ── */}
        <div ref={introRef} style={{
          display: 'flex',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: '40px',
          padding: isMobile ? '32px 6%' : '48px 6%',
          alignItems: 'flex-start',
        }}>
          {/* Left: text */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            <div style={{
              display: 'inline-block',
              backgroundColor: '#e8f0fe',
              color: '#1a56db',
              fontSize: '13px',
              fontWeight: '600',
              padding: '4px 14px',
              borderRadius: '20px',
              marginBottom: '14px',
            }}>
              {detail?.category}
            </div>
            <h1 style={{
              fontSize: isMobile ? '26px' : '38px',
              fontWeight: '800',
              color: '#0a1628',
              margin: '0 0 8px',
              lineHeight: 1.2,
            }}>
              {detail?.title}
            </h1>
            <h2 style={{
              fontSize: isMobile ? '16px' : '20px',
              fontWeight: '500',
              color: '#444',
              margin: '0 0 20px',
            }}>
              {detail?.heading}
            </h2>
            <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.8', maxWidth: '520px' }}>
              {detail?.description}
            </p>
          </div>

          {/* Right: product image */}
          <div style={{
            flex: '0 0 auto',
            width: isMobile ? '100%' : '340px',
            background: '#f4f6fb',
            borderRadius: '14px',
            padding: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <img
              src={mainImage}
              alt="Defibrillator"
              style={{ width: '100%', maxHeight: '260px', objectFit: 'contain', borderRadius: '8px' }}
            />
          </div>
        </div>

        {/* ── KEY FEATURES GRID ── */}
        <div style={{ padding: isMobile ? '16px 6% 40px' : '16px 6% 56px' }}>
          <div style={{ width: '50px', height: '5px', backgroundColor: '#cc0000', borderRadius: '2px', marginBottom: '12px' }} />
          <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#0a1628', marginBottom: '28px' }}>
            Key Features
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '20px',
          }}>
            {Object.entries(sections).map(([sectionName, sectionData], idx) => (
              <div key={idx} style={{ background: '#f4f6fb', borderRadius: '12px', padding: '22px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <span style={{ fontSize: '20px' }}>{sectionData.icon}</span>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0a1628', margin: 0 }}>
                    {sectionName}
                  </h3>
                </div>
                <ul style={{ margin: 0, paddingLeft: '18px', listStyle: 'disc' }}>
                  {sectionData.items.map((item, i) => (
                    <li key={i} style={{ fontSize: '13px', color: '#555', lineHeight: '1.8' }}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* ── RELATED PRODUCTS (rendered only if there are related images) ── */}
        {relatedImages.length > 0 && (
          <div style={{ padding: isMobile ? '0 6% 48px' : '0 6% 60px' }}>
            <div style={{ width: '50px', height: '5px', backgroundColor: '#cc0000', borderRadius: '2px', marginBottom: '12px' }} />
            <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#0a1628', marginBottom: '24px' }}>
              Related Products
            </h2>
            <div style={{
              display: 'flex',
              gap: '20px',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              paddingBottom: '4px',
            }}>
              {relatedImages.map((img, index) => (
                <div
                  key={index}
                  onClick={() => handleImageClick(img)}
                  style={{
                    flex: '0 0 auto',
                    width: '220px',
                    background: '#f4f6fb',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.13)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)';
                  }}
                >
                  <div style={{
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '150px',
                  }}>
                    <img
                      src={img}
                      alt={`Related ${index}`}
                      style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain' }}
                    />
                  </div>
                  <div style={{ background: '#001f4d', padding: '10px 14px' }}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#fff', fontWeight: '600' }}>
                      {imageDetails[img]?.title || `Product ${index + 1}`}
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#aac4ff' }}>
                      {imageDetails[img]?.category}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Footer />

        <style>{`
          *::-webkit-scrollbar { display: none; }
          * { scrollbar-width: none; }
          body { overflow-x: hidden; }
        `}</style>
      </div>
    </>
  );
};

export default DefibrillatorDetail;